import { WavRecorder, WavStreamPlayer } from '../lib/wavtools/index.js';
import { encodeQOA, decodeQOA } from '../utils/audio_codec/qoa';
import { RealtimeUtils } from '@openai/realtime-api-beta';

const LOCAL_RELAY_SERVER_URL: string =
  process.env.REACT_APP_LOCAL_RELAY_SERVER_URL || '';

function uint8ArrayToBase64(uint8Array: Uint8Array): string {
  let binaryString = '';
  for (let i = 0; i < uint8Array.length; i++) {
    binaryString += String.fromCharCode(uint8Array[i]);
  }
  return btoa(binaryString);
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const byteArray = new Uint8Array(binaryString.length);

  for (let i = 0; i < binaryString.length; i++) {
    byteArray[i] = binaryString.charCodeAt(i);
  }
  return byteArray;
}

export function encodePCM16ToBase64(pcm16Data: Int16Array): string {
  if (LOCAL_RELAY_SERVER_URL) {
    const compressedData = encodeQOA(pcm16Data);
    return uint8ArrayToBase64(compressedData);
  } else {
    return RealtimeUtils.arrayBufferToBase64(pcm16Data);
  }
}

export class AudioIO {
  private recorder: WavRecorder;
  private player: WavStreamPlayer;
  private audioPreRecordBuffer: Int16Array[] = [];

  constructor() {
    this.recorder = new WavRecorder({ sampleRate: 24000 });
    this.player = new WavStreamPlayer({ sampleRate: 24000 });
  }

  async connect() {
    if (!this.recorder.processor) {
      await this.recorder.begin();
    }
  }

  async initAudioPlayer() {
    if (!this.player.context) {
      await this.player.connect();
    } else {
      try {
        new AudioWorkletNode(this.player.context, 'stream_processor');
      } catch (error) {
        await this.player.connect();
      }
    }
  }

  setMuted(muted: boolean) {
    this.player.setMuted(muted);
  }

  getMuted() {
    return this.player.isMuted;
  }

  async disconnect() {
    await this.recorder.end();
    await this.player.interrupt();
    this.audioPreRecordBuffer = [];
  }

  async startRecording(callback: (data: ArrayBuffer) => void) {
    if (this.recorder.getStatus() === 'recording') {
      return;  // TODO: already recording, throw error?
    }
    await this.recorder.record((data) => {
      const restructedData = new Int16Array(data.mono);
      try {
        this.commitRecording(callback);
        return callback(restructedData);
      } catch (error) {
        // i.e. client not connected
        this.audioPreRecordBuffer.push(restructedData);
      }
    });
  }

  commitRecording(callback: (data: ArrayBuffer) => void) {
    while (this.audioPreRecordBuffer.length > 0) {
      callback(this.audioPreRecordBuffer[0]);
      this.audioPreRecordBuffer.shift();
    }
  }

  async stopRecording() {
    if (this.recorder.getStatus() === 'recording') {
      await this.recorder.pause();
    }
  }

  getInputFrequencyData() {
    return this.recorder.getStatus() === 'recording'
      ? this.recorder.getFrequencies('voice')
      : { values: new Float32Array([0]) }
  }

  getOutputFrequencyData() {
    return this.player.analyser
      ? this.player.getFrequencies('voice')
      : { values: new Float32Array([0]) };
  }

  async interruptAudioPlayback() {
    return await this.player.interrupt();
  }

  add16BitPCM(arrayBuffer: any, trackId: string) {
    if (arrayBuffer.byteLength < 16) {
      // TODO: this is because the voice server sends very short audio segments.
      // Ignore it for now, but we should handle this better.
      // With bare OpenAI Realtime API, the audio segments won't be short like this.
      return;
    }
    const audioData = LOCAL_RELAY_SERVER_URL
      ? decodeQOA(base64ToUint8Array(RealtimeUtils.arrayBufferToBase64(arrayBuffer)))
      : arrayBuffer;
    this.player.add16BitPCM(audioData, trackId);
  }
}
